import { Helmet } from 'react-helmet'
import React, { useCallback, useEffect, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import Loading from '../../../components/loading'
import NotFound from '../../../components/not-found'
import useApi from '../../../hooks/useApi'
import useSponsorPledgeMinimumCiv from '../../../hooks/useSponsorPledgeMinimumCiv'
import { useCurrentUser } from '../../../hooks/useCurrentUser'
import SponsorPledgeWizard from './SponsorPledgeWizard'

export default function SponsorPledgeFlowPage() {
  const { slug } = useParams()
  const { t } = useTranslation()
  const { currentUser, business, hasBetaFeature } = useCurrentUser()
  const { getEvent, getAdminFee, createTmpSponsorshipPledge, updateBusiness } =
    useApi()

  const [event, setEvent] = useState(null)
  const [loadError, setLoadError] = useState(null)
  const [platformFees, setPlatformFees] = useState(null)

  const fetchEvent = useCallback(async () => {
    if (!slug) return
    try {
      const { data } = await getEvent({ id: slug })
      setEvent(data || null)
      setLoadError(null)
    } catch (e) {
      setLoadError(e?.response?.status ?? 404)
      setEvent(null)
    }
  }, [slug, getEvent])

  useEffect(() => {
    fetchEvent()
  }, [fetchEvent])

  useEffect(() => {
    getAdminFee().then(result => {
      const fees = result?.data?.current_fees
      if (fees) {
        setPlatformFees({
          influencer_fee: fees.influencer_fee,
          civillos_expense_fee: fees.civillos_expense_fee,
          cirrusmio_profit_fee: fees.cirrusmio_profit_fee,
        })
      }
    })
  }, [getAdminFee])

  const { pledgeEvent, minimumPledgeCiv } = useSponsorPledgeMinimumCiv({
    show: Boolean(event?.id),
    event,
  })

  const canUseFlow =
    currentUser?.id && currentUser?.business_id && hasBetaFeature('pf_business')

  if (!currentUser?.id) {
    return <Navigate to="/login" replace />
  }

  if (!canUseFlow) {
    return <Navigate to={`/events/${slug}`} replace />
  }

  if (loadError === 404) {
    return (
      <NotFound
        type="event"
        showAuthLinks={!currentUser?.id}
        suggestAuth={false}
        redirectTo={`/events/${slug}`}
      />
    )
  }

  if (loadError) {
    return (
      <NotFound
        type="event"
        showAuthLinks={!currentUser?.id}
        suggestAuth={false}
        redirectTo={`/events/${slug}`}
      />
    )
  }

  if (!event) {
    return <Loading show />
  }

  if (!event.volunteer_pay_forward) {
    return <Navigate to={`/events/${event.slug ?? event.id}`} replace />
  }

  return (
    <>
      <Helmet>
        <title>
          {t('sponsorPledgeFlow.pageTitle', {
            event: event.title,
          })}
        </title>
      </Helmet>
      <SponsorPledgeWizard
        event={pledgeEvent || event}
        minimumPledgeCiv={minimumPledgeCiv}
        business={business}
        currentUser={currentUser}
        platformFees={platformFees}
        createTmpSponsorshipPledge={createTmpSponsorshipPledge}
        updateBusiness={updateBusiness}
      />
    </>
  )
}
